'use client';
/**
 * B1(2026-09-25) — /blog SSR 이 `cookies()` 를 읽어 매 요청을 dynamic 으로 강제했다.
 *
 * Next.js 는 Server Component 트리 어디서든 `cookies()`/`headers()` 를 «호출만 해도»
 * 그 라우트 전체를 요청 시점 렌더로 못박고, 프레임워크가 응답에 `private, no-store` 를
 * 붙인다 — 이건 middleware 가 세운 `public, s-maxage=300`(src/middleware.ts:613) 보다
 * «나중에» 적용돼 이긴다. (main)/layout.tsx 의 s217 주석이 같은 유형을 이미 문서화했다
 * (거긴 `headers()` 였다) — 여긴 그 주석이 못 덮은 leaf page 자신의 `cookies()` 호출이었다.
 *
 * 실측(2026-09-25, 부하 0): `/blog` 200 · 3.8초 · X-Vercel-Cache: MISS.
 * `export const revalidate = 600`(ISR) 가 걸려 있는데도 매 요청이 콜드였던 이유가 이거다.
 *
 * → 서버는 이제 지역 쿠키를 «보지 않는다». `?region=` 이 없으면 「전체」로 렌더한다
 *   (ISR 가능·캐시 가능). 지역을 저장해 둔 사용자는 여기서 마운트 후 쿠키를 한 번 읽어
 *   `?region=` 을 붙여 클라이언트 라우팅으로 옮긴다 — `BlogRegionCookieSync`(URL→쿠키)의
 *   반대 방향이다.
 *
 * ⚠️ 대가: 저장된 지역이 있는 사용자는 첫 페인트가 «전체» 로 잠깐 보이고 곧바로
 *   지역판(`?region=`)으로 클라이언트 리다이렉트된다(레이아웃 시프트 가능). 쿠키가
 *   없는 사용자(신규·대다수 봇)는 영향이 없다 — 캐시된 「전체」 응답을 그대로 받는다.
 *   지역판(`/blog?region=X`) 자체도 이 파일이 cookies() 를 안 쓰므로 그 쿼리스트링
 *   조합별로 CDN 이 독립 캐시한다(콜드는 지역별 최초 1회뿐).
 */
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { readRegionCookie } from '@/lib/region/cookie';

export default function BlogRegionCookieRestore({ category }: { category: string }) {
  const router = useRouter();
  useEffect(() => {
    const saved = readRegionCookie();
    if (!saved) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('region')) return; // 이미 명시됨 — 손대지 않는다 (무한 루프 방지 겸)
    params.set('category', category);
    params.set('region', saved);
    router.replace(`/blog?${params.toString()}`);
    // category/router 는 마운트 시점 값으로 충분 — region 파라미터가 붙으면 이 컴포넌트는
    // 부모(regionAllowed && !regionRaw) 조건에 의해 다음 렌더에서 아예 마운트되지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
